import React from 'react';
import SummarizeIcon from '@mui/icons-material/Summarize';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ArticleIcon from '@mui/icons-material/Article';

/**
 * 工具類型定義
 */
export const TOOL_TYPES = {
  SCAFFOLD: 'scaffold',    // 鷹架工具（需要節點）
  RESOURCE: 'resource',    // 資源工具（獨立使用）
};

/**
 * 統一的工具配置結構
 * @typedef {Object} ToolConfig
 * @property {string} id - 工具唯一識別碼
 * @property {string} name - 工具顯示名稱
 * @property {string} type - 工具類型 (scaffold/resource)
 * @property {React.Element} icon - 工具圖標
 * @property {string} description - 工具描述
 * @property {number} minNodes - 最少需要的節點數（0 表示不需要）
 * @property {boolean} requiresInput - 是否需要用戶輸入
 * @property {string} [inputLabel] - 輸入框標籤（requiresInput=true 時）
 * @property {string} [inputPlaceholder] - 輸入框提示文字
 * @property {Function} execute - 工具執行函數
 */

/**
 * 鷹架工具定義（需要選擇節點）
 */
export const SCAFFOLD_TOOLS = [
  {
    id: 'summarize',
    name: '摘要',
    type: TOOL_TYPES.SCAFFOLD,
    icon: <SummarizeIcon />,
    description: '將選中的節點內容進行摘要',
    minNodes: 1,
    requiresInput: false,
  },
  {
    id: 'compare',
    name: '比較',
    type: TOOL_TYPES.SCAFFOLD,
    icon: <CompareArrowsIcon />,
    description: '比較選中節點之間的異同',
    minNodes: 2,
    requiresInput: false,
  },
  {
    id: 'question',
    name: '提問',
    type: TOOL_TYPES.SCAFFOLD,
    icon: <QuestionAnswerIcon />,
    description: '根據選中內容生成問題',
    minNodes: 1,
    requiresInput: false,
  },
  {
    id: 'insight',
    name: '洞察',
    type: TOOL_TYPES.SCAFFOLD,
    icon: <LightbulbIcon />,
    description: '從選中內容中發現洞察',
    minNodes: 1,
    requiresInput: false,
  },
  {
    id: 'expand',
    name: '延伸',
    type: TOOL_TYPES.SCAFFOLD,
    icon: <AutoAwesomeIcon />,
    description: '延伸選中內容的想法',
    minNodes: 1,
    requiresInput: false,
  },
];

/**
 * 資源工具定義（獨立使用，不需要節點）
 */
export const RESOURCE_TOOLS = [
  {
    id: 'google-search',
    name: 'Google 搜尋',
    type: TOOL_TYPES.RESOURCE,
    icon: <SearchIcon />,
    description: '搜尋相關資料',
    minNodes: 0,
    requiresInput: true,
    inputLabel: '搜尋關鍵字',
    inputPlaceholder: '請輸入要搜尋的內容...',
  },
  {
    id: 'openalex',
    name: 'OpenAlex',
    type: TOOL_TYPES.RESOURCE,
    icon: <LibraryBooksIcon />,
    description: '開放學術資料庫（超過 2.5 億篇）',
    minNodes: 0,
    requiresInput: true,
    inputLabel: '論文關鍵字',
    inputPlaceholder: '例：machine learning, psychology...',
  },
  {
    id: 'crossref',
    name: 'Crossref',
    type: TOOL_TYPES.RESOURCE,
    icon: <ArticleIcon />,
    description: '搜尋 DOI 論文元資料',
    minNodes: 0,
    requiresInput: true,
    inputLabel: '論文關鍵字',
    inputPlaceholder: '例：climate change, education...',
  },
];

/**
 * 所有工具的統一配置
 */
export const ALL_TOOLS = [...SCAFFOLD_TOOLS, ...RESOURCE_TOOLS];

/**
 * 根據 ID 獲取工具配置
 */
export const getToolById = (toolId) => {
  return ALL_TOOLS.find(tool => tool.id === toolId);
};

/**
 * 根據類型獲取工具列表
 */
export const getToolsByType = (type) => {
  return ALL_TOOLS.filter(tool => tool.type === type);
};
